import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs/Observable';
import { BehaviorSubject } from 'rxjs/BehaviorSubject';
import { MessageService } from '@services/helpers/message-service/message.service';
import {
	PanelsPositionObject,
	ServerDataResponse,
	TimeSettingsObject
} from '@models/models';
import { DateTimeSettings } from '@models/settings.model';
import { HttpServiceProvider } from '@services/helpers/http.service';

@Injectable()
export class SettingsProvider {
	private timeFormatSettingsForSave: TimeSettingsObject;
	private timeFormatSettingsBS: BehaviorSubject<any> = new BehaviorSubject({
		weekStartsOn: null,
		dateFormat: null,
		timeFormat: null,
		fundyIntegration: null
	});
	private panelsPosition: BehaviorSubject<
		PanelsPositionObject
	> = new BehaviorSubject({
		panelsPosition: null
	});

	constructor(
		public http: HttpClient,
		private httpService: HttpServiceProvider,
		private messageService: MessageService
	) {}

	getTimezoneList(): Observable<any> {
		const url = '/public/rest/timezones';
		return this.http.get(url);
	}

	getCurrencyList(): Observable<any> {
		const url = '/public/rest/currencies';
		return this.http.get(url);
	}

	getCurrencyAndTaxes(): Observable<any> {
		const url = '/protected/rest/currency_and_taxes';
		return this.http.get(url);
	}

	setDefaultTimeFormatSettings(): any {
		const defaultTimeFormatSettings = {
			weekStartsOn: 'MONDAY',
			dateFormat: 'DD_MM_YYYY',
			timeFormat: 'T_12'
		};
		this.timeFormatSettingsBS.next(defaultTimeFormatSettings);
	}

	getDateTimeSettingsV1(): Observable<ServerDataResponse<DateTimeSettings>> {
		const url = '/api/user/v1/settings/datetime';
		return this.http.get<ServerDataResponse<DateTimeSettings>>(url);
	}

	updateDateTimeSettings(url, data) {
		this.timeFormatSettingsForSave = {
			...this.timeFormatSettingsForSave,
			weekStartsOn: data.weekStartsOn,
			dateFormat: data.dateFormat,
			timeFormat: data.timeFormat
		};
		return this.http.post(url, data);
	}

	getSettingsRequest(): any {
		const url = '/api/user/settings/bootstrap';

		return this.http
			.get(url)
			.catch(error => {
				this.messageService.showToaster('e_0', 'general', error.error);
				return Observable.throw(error);
			})
			.map((data: any) => {
				const timeFormatSettings = {
					weekStartsOn: data.data['user.settings.calendar.weekStartsOn'],
					dateFormat: data.data['user.settings.calendar.dateFormat'],
					timeFormat: data.data['user.settings.calendar.timeFormat'],
					fundyIntegration:
						data.data['company.settings.integration.fundy.state'],
					fundyAvailable:
						data.data['company.settings.integration.fundy.available'],
					contactFormVersion: data.data['company.settings.contactform.version']
				};

				this.setPanelsPosition(
					data.data['company.settings.dashboard.panels.position']
				);

				this.timeFormatSettingsBS.next(timeFormatSettings);
			});
	}

	private setPanelsPosition(panelsPosition) {
		this.panelsPosition.next(panelsPosition);
	}

	get timeFormatSettings() {
		return this.timeFormatSettingsBS.asObservable();
	}

	setTimeFormatSettingsFromCP(data: any) {
		this.timeFormatSettingsBS.next(data);
	}

	get panelsPositionObservable() {
		return this.panelsPosition.asObservable();
	}

	savePanelsPosition(panelsPosition) {
		const settingsToSend = [
			{
				name: 'company.settings.dashboard.panels.position',
				jsonValue: {
					panels: panelsPosition
				}
			}
		];
		this.setPanelsPosition({ panels: panelsPosition });

		const url = '/api/user/settings/bootstrap/batch-update';

		return this.http.post(url, settingsToSend);
	}

	dateFormatPattern(dateFormat = 'DD_MM_YYYY', weekStartsOn = 'MONDAY') {
		const format = {
			dateFormat,
			timeFormat: 'T_12',
			weekStartsOn
		};
		this.timeFormatSettingsBS.next(format);
	}

	updateTimeFormatSettings(settings: TimeSettingsObject) {
		if (settings === undefined) {
			return;
		}

		this.timeFormatSettingsForSave = settings;

		const settingsToSend = [
			{
				name: 'user.settings.calendar.timeFormat',
				value: settings.timeFormat
			},
			{
				name: 'user.settings.calendar.dateFormat',
				value: settings.dateFormat
			},
			{
				name: 'user.settings.calendar.weekStartsOn',
				value: settings.weekStartsOn
			}
		];

		const url = '/api/user/settings/bootstrap/batch-update';

		return this.http.post(url, settingsToSend);
	}

	applySavedSettings(): void {
		this.timeFormatSettingsBS.next(this.timeFormatSettingsForSave);
	}

	updateFundyIntegration(enableFundy: boolean): Observable<any> {
		const state = enableFundy ? 'ENABLED' : 'DISABLED';
		const url = '/api/user/settings/bootstrap/update ';

		const stateIntegrationToSend = {
			name: 'company.settings.integration.fundy.state',
			value: state
		};

		return this.http
			.post(url, stateIntegrationToSend)
			.catch(error => {
				this.messageService.showToaster('e_0', 'general', error);
				return Observable.throw(error);
			})
			.switchMap(() => this.timeFormatSettings)
			.first()
			.map(settings => {
				settings.fundyIntegration = state;
				this.timeFormatSettingsBS.next(settings);
				return settings;
			});
	}
}



// WEBPACK FOOTER //
// ./node_modules/angular2-template-loader!./node_modules/string-replace-webpack-plugin/loader.js?id=kkml25e9zf!./shared/services/settings/settings.ts